import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { DeliverableCard } from '../components/DeliverableCard'

interface Evidence {
  id: string
  title: string
  evidence_type: 'file' | 'link' | 'commit' | 'activity'
  path: string | null
  relationship: 'contributes' | 'completes'
  status: 'suggested' | 'confirmed' | 'rejected'
  ai_interpretation: string | null
  ai_suggestion_reason: string | null
  ai_confidence: number | null
  added_by: { id: string; name: string } | null
  added_at: string | null
  deliverable_id: string | null
}

interface Deliverable {
  id: string
  goal_id: string
  title: string
  status: 'not_started' | 'in_progress' | 'complete'
  due_date: string | null
  start_date: string | null
  verification: string | null
  ai_summary: string | null
  ai_confidence: number | null
  ai_assessed_at: string | null
  ai_suggests_complete: boolean
  evidence?: Evidence[]
  suggested_evidence?: Evidence[]
  assignee?: { id: string; name: string } | null
}

interface Goal {
  id: string
  title: string
  description: string | null
  status: 'not_started' | 'in_progress' | 'at_risk' | 'complete'
  due_date: string | null
  owner?: { id: string; name: string }
  assignee?: { id: string; name: string }
  project?: { id: string; name: string; project_id: string } | null
  deliverables?: Deliverable[]
}

interface Person {
  id: string
  name: string
  email: string
  title: string | null
  role: string | null
}

interface ActivityEvent {
  timestamp: string
  event_type: string
  title: string
  description: string
  author_name: string
  author_email: string
  source_name: string
  source_type: string
  artifact_path: string
  url: string
  project_id: string
  project_name: string
}

interface ActivityResponse {
  events: ActivityEvent[]
  projects: { id: string; name: string }[]
}

interface Suggestion {
  deliverable_id: string
  confidence: number
  reason: string
  relationship: 'contributes' | 'completes'
}

interface SuggestResponse {
  suggestions: Suggestion[]
}


const statusColors: Record<string, string> = {
  not_started: 'bg-gray-800 text-gray-300',
  in_progress: 'bg-blue-900/50 text-blue-300',
  at_risk: 'bg-red-900/50 text-red-300',
  complete: 'bg-green-900/50 text-green-300',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function getDaysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null
  const due = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

function getDueDateBadge(dateStr: string | null, status: string) {
  if (status === 'complete') return null
  const days = getDaysUntil(dateStr)
  if (days === null) return null

  if (days < 0) {
    return <span className="text-xs px-1.5 py-0.5 bg-red-900/50 text-red-300 rounded">{Math.abs(days)}d overdue</span>
  } else if (days === 0) {
    return <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">Due today</span>
  } else if (days <= 7) {
    return <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">{days}d left</span>
  }
  return null
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function SuggestMatchModal({
  activity,
  deliverables,
  onClose,
  onConfirm,
}: {
  activity: ActivityEvent
  deliverables: Array<{ id: string; title: string; goal?: { title: string } }>
  onClose: () => void
  onConfirm: (deliverableId: string, relationship: string) => void
}) {
  const [suggesting, setSuggesting] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedRel, setSelectedRel] = useState<string>('contributes')
  const [error, setError] = useState<string | null>(null)

  // Run suggestion on mount
  useState(() => {
    setSuggesting(true)
    api.post<SuggestResponse>('/suggest', {
      activity: {
        type: activity.event_type === 'commit' ? 'commit' : 'file',
        title: activity.title,
        path: activity.artifact_path || activity.url,
      },
      create_suggestions: false,
    })
      .then((res) => {
        setSuggestions(res.suggestions || [])
        if (res.suggestions?.length > 0) {
          setSelectedId(res.suggestions[0].deliverable_id)
          setSelectedRel(res.suggestions[0].relationship)
        }
      })
      .catch((e) => {
        setError('Failed to get suggestions')
        console.error(e)
      })
      .finally(() => setSuggesting(false))
  })

  const handleConfirm = () => {
    if (selectedId) {
      onConfirm(selectedId, selectedRel)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-medium text-lg mb-2">Match Activity to Deliverable</h3>
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          {activity.title}
        </p>

        {suggesting ? (
          <p className="text-[var(--color-text-secondary)] text-center py-8">
            Analyzing activity...
          </p>
        ) : error ? (
          <p className="text-red-400 text-center py-4">{error}</p>
        ) : (
          <>
            <div className="mb-4">
              <label className="text-xs text-[var(--color-text-secondary)] mb-2 block">
                {suggestions.length > 0 ? 'AI Suggestions:' : 'Select a deliverable:'}
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {suggestions.length > 0 ? (
                  suggestions.map((s) => {
                    const d = deliverables.find((del) => del.id === s.deliverable_id)
                    return (
                      <label
                        key={s.deliverable_id}
                        className={`flex items-start gap-3 p-3 rounded cursor-pointer ${
                          selectedId === s.deliverable_id
                            ? 'bg-[var(--color-accent)]/20 border border-[var(--color-accent)]'
                            : 'bg-[var(--color-surface)] border border-transparent hover:border-[var(--color-border)]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="deliverable"
                          checked={selectedId === s.deliverable_id}
                          onChange={() => {
                            setSelectedId(s.deliverable_id)
                            setSelectedRel(s.relationship)
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{d?.title || 'Unknown'}</span>
                            <span className="text-xs text-[var(--color-accent)]">
                              {Math.round(s.confidence * 100)}% match
                            </span>
                          </div>
                          <p className="text-xs text-purple-400 mt-1">
                            {s.reason}
                          </p>
                        </div>
                      </label>
                    )
                  })
                ) : (
                  deliverables.map((d) => (
                    <label
                      key={d.id}
                      className={`flex items-start gap-3 p-3 rounded cursor-pointer ${
                        selectedId === d.id
                          ? 'bg-[var(--color-accent)]/20 border border-[var(--color-accent)]'
                          : 'bg-[var(--color-surface)] border border-transparent hover:border-[var(--color-border)]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="deliverable"
                        checked={selectedId === d.id}
                        onChange={() => setSelectedId(d.id)}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">{d.title}</span>
                        {d.goal && (
                          <span className="text-xs text-[var(--color-text-secondary)] ml-2">
                            ({d.goal.title})
                          </span>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="text-xs text-[var(--color-text-secondary)] mb-2 block">
                Relationship:
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="relationship"
                    value="contributes"
                    checked={selectedRel === 'contributes'}
                    onChange={() => setSelectedRel('contributes')}
                  />
                  <span className="text-sm">Contributes to</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="relationship"
                    value="completes"
                    checked={selectedRel === 'completes'}
                    onChange={() => setSelectedRel('completes')}
                  />
                  <span className="text-sm">Completes</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedId}
                className="btn btn-primary"
              >
                Add as Evidence
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function MyWork() {
  const [showCompleted, setShowCompleted] = useState(false)
  const [matchingActivity, setMatchingActivity] = useState<ActivityEvent | null>(null)
  const queryClient = useQueryClient()

  // Get current person's info
  const { data: me } = useQuery({
    queryKey: ['people', 'me'],
    queryFn: () => api.get<Person>('/people/me'),
  })

  // Get goals with deliverables and evidence
  const { data: goals, isLoading: goalsLoading } = useQuery({
    queryKey: ['people', 'me', 'goals', 'with-evidence'],
    queryFn: () => api.get<Goal[]>('/people/me/goals?include_deliverables=true&include_evidence=true'),
  })

  // Get pending suggestions
  const { data: suggestions } = useQuery({
    queryKey: ['me', 'suggestions'],
    queryFn: () => api.get<Evidence[]>('/me/suggestions'),
  })

  // Get recent activity
  const { data: activityData } = useQuery({
    queryKey: ['me', 'activity'],
    queryFn: () => api.get<ActivityResponse>('/people/me/activity?limit=20&days=14'),
  })

  // Confirm/reject suggestion mutation
  const updateSuggestionMutation = useMutation({
    mutationFn: ({ evidenceId, status }: { evidenceId: string; status: string }) =>
      api.patch(`/evidence/${evidenceId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['people', 'me', 'goals'] })
    },
  })

  // Add evidence from activity mutation
  const addEvidenceMutation = useMutation({
    mutationFn: ({ deliverableId, activity, relationship }: {
      deliverableId: string
      activity: ActivityEvent
      relationship: string
    }) =>
      api.post(`/deliverables/${deliverableId}/evidence`, {
        title: activity.title,
        evidence_type: activity.event_type === 'commit' ? 'commit' : 'file',
        path: activity.url || activity.artifact_path,
        relationship,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people', 'me', 'goals'] })
      setMatchingActivity(null)
    },
  })

  if (goalsLoading) {
    return (
      <div className="max-w-4xl">
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    )
  }

  // Separate active and completed goals
  const activeGoals = goals?.filter(g => g.status !== 'complete') || []
  const completedGoals = goals?.filter(g => g.status === 'complete') || []

  // Calculate stats
  const totalDeliverables = goals?.reduce((sum, g) => sum + (g.deliverables?.length || 0), 0) || 0
  const completedDeliverables = goals?.reduce(
    (sum, g) => sum + (g.deliverables?.filter(d => d.status === 'complete').length || 0),
    0
  ) || 0
  const pendingSuggestions = suggestions?.length || 0

  // Get all deliverables with their AI assessment status
  const allDeliverables = goals?.flatMap(g =>
    g.deliverables?.map(d => ({ ...d, goal: g })) || []
  ) || []
  const needsAssessment = allDeliverables.filter(d =>
    d.status !== 'complete' && !d.ai_assessed_at
  ).length

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl">My Work</h1>
          {me && (
            <p className="text-[var(--color-text-secondary)] mt-1">
              {me.title || me.role || me.email}
            </p>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="card text-center">
          <div className="text-3xl font-medium">{activeGoals.length}</div>
          <div className="text-sm text-[var(--color-text-secondary)]">Active Goals</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-medium">{completedDeliverables}/{totalDeliverables}</div>
          <div className="text-sm text-[var(--color-text-secondary)]">Deliverables</div>
        </div>
        {pendingSuggestions > 0 && (
          <div className="card text-center border-yellow-900/50">
            <div className="text-3xl font-medium text-yellow-400">{pendingSuggestions}</div>
            <div className="text-sm text-[var(--color-text-secondary)]">Needs Review</div>
          </div>
        )}
        {needsAssessment > 0 && (
          <div className="card text-center border-purple-900/50">
            <div className="text-3xl font-medium text-purple-400">{needsAssessment}</div>
            <div className="text-sm text-[var(--color-text-secondary)]">Not Assessed</div>
          </div>
        )}
      </div>

      {/* Pending Suggestions Alert */}
      {pendingSuggestions > 0 && (
        <section className="mb-8">
          <h2 className="section-label text-yellow-400">⚠ Review Suggested Evidence</h2>
          <div className="card">
            <p className="text-sm text-[var(--color-text-secondary)] mb-3">
              AI has suggested {pendingSuggestions} item{pendingSuggestions !== 1 ? 's' : ''} that may relate to your deliverables.
              Review and confirm or reject each suggestion.
            </p>
            <div className="space-y-2">
              {suggestions?.slice(0, 5).map((s) => {
                const deliverable = allDeliverables.find(d => d.id === s.deliverable_id)
                return (
                  <div key={s.id} className="flex items-center justify-between bg-[var(--color-surface)] p-3 rounded">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.title}</div>
                      {deliverable && (
                        <div className="text-xs text-[var(--color-text-secondary)]">
                          → {deliverable.title}
                        </div>
                      )}
                      {s.ai_suggestion_reason && (
                        <div className="text-xs text-purple-400 mt-1">
                          🤖 {s.ai_suggestion_reason}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {s.ai_confidence && (
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          {Math.round(s.ai_confidence * 100)}%
                        </span>
                      )}
                      <button
                        onClick={() => updateSuggestionMutation.mutate({ evidenceId: s.id, status: 'confirmed' })}
                        className="px-3 py-1 text-sm bg-green-900/50 text-green-300 rounded hover:bg-green-900/70"
                        disabled={updateSuggestionMutation.isPending}
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => updateSuggestionMutation.mutate({ evidenceId: s.id, status: 'rejected' })}
                        className="px-3 py-1 text-sm bg-red-900/50 text-red-300 rounded hover:bg-red-900/70"
                        disabled={updateSuggestionMutation.isPending}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                )
              })}
              {pendingSuggestions > 5 && (
                <p className="text-xs text-[var(--color-text-secondary)] text-center pt-2">
                  And {pendingSuggestions - 5} more...
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Recent Activity */}
      {activityData?.events && activityData.events.length > 0 && (
        <section className="mb-8">
          <h2 className="section-label">Recent Activity</h2>
          <div className="card">
            <p className="text-sm text-[var(--color-text-secondary)] mb-3">
              Activity from your projects. Click "Suggest Match" to link to a deliverable.
            </p>
            <div className="space-y-2">
              {activityData.events.slice(0, 10).map((event, idx) => (
                <div
                  key={`${event.timestamp}-${idx}`}
                  className="flex items-center justify-between bg-[var(--color-surface)] p-3 rounded"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--color-bg)]">
                        {event.event_type}
                      </span>
                      <span className="text-sm font-medium truncate">{event.title}</span>
                    </div>
                    <div className="flex gap-3 text-xs text-[var(--color-text-secondary)] mt-1">
                      <span>{event.project_name}</span>
                      <span>{event.source_name}</span>
                      <span>{formatRelativeTime(event.timestamp)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setMatchingActivity(event)}
                    className="ml-4 px-3 py-1 text-sm text-[var(--color-accent)] hover:bg-[var(--color-accent)]/10 rounded"
                  >
                    Suggest Match →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Suggest Match Modal */}
      {matchingActivity && (
        <SuggestMatchModal
          activity={matchingActivity}
          deliverables={allDeliverables.filter(d => d.status !== 'complete').map(d => ({
            id: d.id,
            title: d.title,
            goal: d.goal ? { title: d.goal.title } : undefined,
          }))}
          onClose={() => setMatchingActivity(null)}
          onConfirm={(deliverableId, relationship) => {
            addEvidenceMutation.mutate({
              deliverableId,
              activity: matchingActivity,
              relationship,
            })
          }}
        />
      )}

      {/* Active Goals */}
      <section className="mb-8">
        <h2 className="section-label">Active Goals</h2>
        {activeGoals.length > 0 ? (
          <div className="space-y-6">
            {activeGoals.map((goal) => (
              <div key={goal.id} className="card">
                {/* Goal Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{goal.title}</h3>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${statusColors[goal.status]}`}>
                        {goal.status.replace('_', ' ')}
                      </span>
                      {getDueDateBadge(goal.due_date, goal.status)}
                    </div>
                    {goal.description && (
                      <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                        {goal.description}
                      </p>
                    )}
                    <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mt-2">
                      {goal.due_date && <span>Due: {formatDate(goal.due_date)}</span>}
                      {goal.project && <span>Project: {goal.project.name}</span>}
                      {goal.owner && <span>Owner: {goal.owner.name}</span>}
                    </div>
                  </div>
                </div>

                {/* Deliverables */}
                {goal.deliverables && goal.deliverables.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-[var(--color-border)]">
                    <h4 className="text-xs font-medium text-[var(--color-text-secondary)] uppercase">
                      Deliverables
                    </h4>
                    {goal.deliverables.map((d) => (
                      <DeliverableCard
                        key={d.id}
                        deliverable={d}
                        type="personal"
                        onUpdate={() => queryClient.invalidateQueries({ queryKey: ['people', 'me', 'goals'] })}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <p className="text-[var(--color-text-secondary)]">
              No active goals. Contact your manager to set up your goals.
            </p>
          </div>
        )}
      </section>

      {/* Completed Goals (collapsible) */}
      {completedGoals.length > 0 && (
        <section>
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="section-label text-[var(--color-text-secondary)] hover:text-[var(--color-text)] cursor-pointer"
          >
            {showCompleted ? '▼' : '▶'} Completed ({completedGoals.length})
          </button>
          {showCompleted && (
            <div className="space-y-4 mt-4">
              {completedGoals.map((goal) => (
                <div key={goal.id} className="card opacity-75">
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <h3 className="font-medium">{goal.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
